// Generic module API — Laufräder, Baugruppen, Montage share this shape.
// Drückteile hat sein eigenes Client-API (abweichende Spezialfelder).

import type { Paginated, Kunde, User, StandWert } from './api';

export interface ModuleItem {
  id: number;
  bestellnr: string;
  dv_nummer: string | null;
  position: string | null;
  sachbearbeiter_id: number | null;
  sachbearbeiter_name?: string;
  bezeichnung: string | null;
  zeichnungs_nr: string | null;
  stueck: number;
  kunde_id: number | null;
  kunde_name?: string;
  lieferdatum: string;
  lieferdatum_minus3: string;
  kw: number;
  jahr: number;
  zeit_pro_stueck: number | null;
  gesamtzeit: number;
  kommentar: string | null;
  priority: 'normal' | 'eilig' | 'kritisch';
  fertig: number;
  fertigungsdatum: string | null;
  stand_id: number | null;
  stand_wert: string | null;
  stand_farbe: string | null;
  abgerechnet: number;
  vorkasse: number;
  vorkasse_bezahlt_am: string | null;
  created_at: string;
  updated_at: string;
  current_session?: string | null;  // JSON string
  // Modül-spezifische Felder dinamik geliyor
  [key: string]: unknown;
}

const API_BASE = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE ?? '');

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(import.meta.env.DEV && { 'X-Dev-User': 'hamdi@sickinger.de' }),
      ...options.headers,
    },
  });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /**/ }
    const msg = (body as { error?: string })?.error ?? `API ${res.status}: ${path}`;
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export function moduleApi(modulePath: 'laufraeder' | 'baugruppen' | 'montage' | 'laser_kant') {
  const base = `/api/${modulePath}`;
  return {
    list: (params: Record<string, string | number | boolean | undefined> = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
      }
      return request<Paginated<ModuleItem>>(`${base}?${q}`);
    },
    get:    (id: number) => request<{ data: ModuleItem }>(`${base}/${id}`),
    create: (body: Partial<ModuleItem>) =>
      request<{ data: ModuleItem }>(base, { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<ModuleItem>) =>
      request<{ data: ModuleItem }>(`${base}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: number) =>
      request<{ data: { id: number; deleted: boolean } }>(`${base}/${id}`, { method: 'DELETE' }),
    history: (id: number) => request<{ data: unknown[] }>(`${base}/${id}/history`),
  };
}

// Convenience lookups via existing endpoints
export async function fetchLookups(): Promise<{ kunden: Kunde[]; users: User[]; standWerte: StandWert[] }> {
  const [k, u, s] = await Promise.all([
    request<{ data: Kunde[] }>('/api/lookup/kunden'),
    request<{ data: User[] }>('/api/lookup/users'),
    request<{ data: StandWert[] }>('/api/lookup/stand-werte'),
  ]);
  return { kunden: k.data, users: u.data, standWerte: s.data };
}
